import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardService } from '../../services/dashboardService';
import { PieChart, ArrowRight, AlertTriangle } from 'lucide-react';

const CaseAllocationWidget = () => {
  const [allocations, setAllocations] = useState([]);
  const [totals, setTotals] = useState({
    total_allocated: 0,
    total_used: 0,
    total_available: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCaseAllocations();
  }, []);

  const fetchCaseAllocations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardService.getCaseAllocationSummary(5);
      setAllocations(response.data.allocations || []);
      setTotals({
        total_allocated: response.data.total_allocated || 0,
        total_used: response.data.total_used || 0,
        total_available: response.data.total_available || 0
      });
    } catch (error) {
      console.error('Error fetching case allocations:', error);
      setError('Failed to load allocations');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 90) return { bar: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' };
    if (percentage >= 75) return { bar: 'bg-yellow-500', text: 'text-yellow-700', bg: 'bg-yellow-50' };
    if (percentage >= 50) return { bar: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50' };
    return { bar: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-50' };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 w-40 bg-gray-200 rounded"></div>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i}>
                <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
                <div className="h-2 bg-gray-100 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <PieChart className="mr-2 h-5 w-5 text-yellow-600" />
            Case Allocations
          </h3>
          <button
            onClick={fetchCaseAllocations}
            className="text-yellow-600 hover:text-gray-800 text-sm"
          >
            Retry
          </button>
        </div>
        <div className="text-center py-4">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const overallUtilization = totals.total_allocated > 0
    ? (totals.total_used / totals.total_allocated * 100).toFixed(0)
    : 0;

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <PieChart className="mr-2 h-5 w-5 text-yellow-600" />
            Case Allocations
          </h3>
          <Link
            to="/trust-accounts"
            className="text-yellow-600 hover:text-gray-800 flex items-center text-sm font-medium"
          >
            View All
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Overall Utilization</span>
          <span className="text-sm font-bold text-gray-900">{overallUtilization}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${getUtilizationColor(overallUtilization).bar}`}
            style={{ width: `${Math.min(100, overallUtilization)}%` }}
          ></div>
        </div>
        <div className="mt-2 flex justify-between text-xs text-gray-500">
          <span>Used: {formatCurrency(totals.total_used)}</span>
          <span>Allocated: {formatCurrency(totals.total_allocated)}</span>
        </div>
      </div>

      {/* Allocation List */}
      <div className="p-4">
        {allocations.length === 0 ? (
          <div className="text-center py-6">
            <PieChart className="mx-auto h-8 w-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No case allocations found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {allocations.map((allocation) => {
              const colors = getUtilizationColor(allocation.utilization_percentage);
              return (
                <Link
                  key={allocation.id}
                  to={`/cases/${allocation.case_id}`}
                  className="block hover:bg-gray-50 rounded-lg p-2 -mx-2 transition-colors"
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {allocation.case_number}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {allocation.client_name}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${colors.bg} ${colors.text}`}>
                        {allocation.utilization_percentage.toFixed(0)}%
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-500 ${colors.bar}`}
                      style={{ width: `${Math.min(100, allocation.utilization_percentage)}%` }}
                    ></div>
                  </div>

                  {/* Amounts */}
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Used: {formatCurrency(allocation.used_amount)}</span>
                    <span>of {formatCurrency(allocation.allocated_amount)}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Available Funds Summary */}
      {totals.total_available > 0 && (
        <div className="p-4 bg-green-50 border-t border-green-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-green-900">Available for Use</span>
            <span className="text-sm font-bold text-green-900">
              {formatCurrency(totals.total_available)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseAllocationWidget;
